import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";

import { listUnifiedCatalog } from "@/lib/catalog.functions";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { ExploraExperience } from "@/components/explora/ExploraExperience";
import { normalizeLang } from "@/i18n";

const catalogQuery = (lang: string) =>
  queryOptions({
    queryKey: ["catalog", "unified", lang],
    queryFn: () => listUnifiedCatalog({ data: { lang } }),
    staleTime: 5 * 60 * 1000,
  });

export const Route = createFileRoute("/explora")({
  head: () => ({
    meta: [
      { title: "Explora México — mapa interactivo de destinos" },
      {
        name: "description",
        content:
          "Descubre México a través de un mapa interactivo. Filtra por playas, cultura, arqueología y más, o deja que la IA te recomiende destinos.",
      },
      { property: "og:title", content: "Explora México" },
      {
        property: "og:description",
        content: "Mapa interactivo de destinos con recomendaciones IA.",
      },
    ],
    links: [{ rel: "canonical", href: "https://www.mostrandomexico.mx/explora" }],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(catalogQuery(context.lang)),
  component: ExploraPage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-center text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</div>
  ),
  notFoundComponent: () => (
    <div className="p-8 text-center text-sm text-muted-foreground">No encontrado.</div>
  ),
});

function ExploraPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <Suspense fallback={<div className="h-screen" />}>
        <ExploraBody />
      </Suspense>
      <SiteFooter />
    </div>
  );
}

function ExploraBody() {
  const { i18n } = useTranslation();
  const lang = normalizeLang(i18n.language) as "es" | "en";
  const { data } = useSuspenseQuery(catalogQuery(lang));
  const isEs = lang === "es";

  return (
    <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <header className="mb-6">
        <h1 className="font-display text-4xl font-semibold tracking-tight sm:text-5xl">
          {isEs ? "Explora México" : "Explore Mexico"}
        </h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          {isEs
            ? "Un mapa interactivo de destinos turísticos. Filtra por tipo de viaje o deja que la IA te recomiende según tu estilo."
            : "An interactive map of destinations. Filter by trip type or let AI recommend based on your style."}
        </p>
      </header>
      <ExploraExperience tours={data.tours} lang={lang} />
    </main>
  );
}
